import { API_URL } from '@/config';
import { formatDate } from '@/utils/dateFormat';
import React, { useState, useEffect } from 'react';
import { Button } from '@/components/ui/button';
import { Card, CardContent } from '@/components/ui/card';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { BookOpen, Trash2, Edit, Plus, Image as ImageIcon, Video, FileText, Link2, ExternalLink, Eye, EyeOff, Tag } from 'lucide-react';
import axios from 'axios';
import { toast } from 'sonner';
import { getImageUrl } from '@/utils/imageUrl';

const API = `${API_URL}/api`;

const AdminBlogsPage = () => {
  const [blogs, setBlogs] = useState([]);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editMode, setEditMode] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [tagInput, setTagInput] = useState('');
  const [formData, setFormData] = useState({
    blog_id: '',
    title: '',
    description: '',
    content: '',
    type: 'article',
    media_url: '',
    thumbnail_url: '',
    external_link: '',
    tags: [],
    is_active: true
  });

  useEffect(() => { loadBlogs(); }, []);

  const loadBlogs = async () => {
    try {
      const response = await axios.get(`${API}/blogs?active_only=false`, {
        headers: { Authorization: `Bearer ${localStorage.getItem("session_token")}` }
      });
      setBlogs(response.data);
    } catch (error) {
      toast.error('Failed to load blogs');
    }
  };

  const resetForm = () => {
    setFormData({
      blog_id: '',
      title: '',
      description: '',
      content: '',
      type: 'article',
      media_url: '',
      thumbnail_url: '',
      external_link: '',
      tags: [],
      is_active: true
    });
    setTagInput('');
    setEditMode(false);
  };

  const handleFileUpload = async (file, field) => {
    if (!file) return;
    
    // Check file size (max 10MB)
    const maxSize = 10 * 1024 * 1024;
    if (file.size > maxSize) {
      toast.error(`File too large. Maximum size is 10MB. Your file: ${(file.size / (1024 * 1024)).toFixed(1)}MB`);
      return;
    }
    
    setUploading(true);
    try {
      const uploadFormData = new FormData();
      uploadFormData.append('file', file);
      
      const response = await axios.post(`${API}/upload`, uploadFormData, {
        headers: { 
          Authorization: `Bearer ${localStorage.getItem("session_token")}`,
          'Content-Type': 'multipart/form-data'
        },
        timeout: 60000
      });
      
      setFormData(prev => ({ ...prev, [field]: response.data.url }));
      toast.success('File uploaded successfully!');
    } catch (error) {
      console.error('Upload error:', error);
      if (error.response?.status === 413) {
        toast.error('File too large for server. Try compressing the image.');
      } else {
        toast.error(error.response?.data?.detail || 'Failed to upload file');
      }
    } finally {
      setUploading(false);
    }
  };

  const addTag = () => {
    if (tagInput.trim() && !formData.tags.includes(tagInput.trim())) {
      setFormData(prev => ({ ...prev, tags: [...prev.tags, tagInput.trim()] }));
      setTagInput('');
    }
  };

  const removeTag = (tag) => {
    setFormData(prev => ({ ...prev, tags: prev.tags.filter(t => t !== tag) }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    try {
      const payload = {
        title: formData.title,
        description: formData.description,
        content: formData.content || null,
        type: formData.type,
        media_url: formData.media_url || null,
        thumbnail_url: formData.thumbnail_url || null,
        external_link: formData.external_link || null,
        tags: formData.tags,
        is_active: formData.is_active
      };

      if (editMode) {
        await axios.put(`${API}/blogs/${formData.blog_id}`, payload, {
          headers: { Authorization: `Bearer ${localStorage.getItem("session_token")}` }
        });
        toast.success('Blog updated successfully!');
      } else {
        await axios.post(`${API}/blogs`, payload, {
          headers: { Authorization: `Bearer ${localStorage.getItem("session_token")}` }
        });
        toast.success('Blog created successfully!');
      }
      
      setDialogOpen(false);
      resetForm();
      loadBlogs();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to save blog');
    }
  };

  const handleEdit = (item) => {
    setFormData({
      blog_id: item.blog_id,
      title: item.title || '',
      description: item.description || '',
      content: item.content || '',
      type: item.type || 'article',
      media_url: item.media_url || '',
      thumbnail_url: item.thumbnail_url || '',
      external_link: item.external_link || '',
      tags: item.tags || [],
      is_active: item.is_active !== false
    });
    setEditMode(true);
    setDialogOpen(true);
  };

  const handleDelete = async (blogId) => {
    if (!confirm('Are you sure you want to delete this blog?')) return;
    
    try {
      await axios.delete(`${API}/blogs/${blogId}`, {
        headers: { Authorization: `Bearer ${localStorage.getItem("session_token")}` }
      });
      toast.success('Blog deleted successfully!');
      loadBlogs();
    } catch (error) {
      toast.error('Failed to delete blog');
    }
  };

  const toggleActive = async (item) => {
    try {
      await axios.put(`${API}/blogs/${item.blog_id}`, { is_active: !item.is_active }, {
        headers: { Authorization: `Bearer ${localStorage.getItem("session_token")}` }
      });
      toast.success(`Blog ${item.is_active ? 'hidden' : 'published'}!`);
      loadBlogs();
    } catch (error) {
      toast.error('Failed to update blog');
    }
  };

  const getTypeIcon = (type) => {
    switch (type) {
      case 'photo': return <ImageIcon className="w-4 h-4" />;
      case 'video': return <Video className="w-4 h-4" />;
      case 'link': return <Link2 className="w-4 h-4" />;
      default: return <FileText className="w-4 h-4" />;
    }
  };

  return (
    <div className="space-y-6">
      <div className="flex justify-between items-center">
        <div>
          <h2 className="text-2xl font-bold" style={{ color: '#0F172A' }}>Education Blogs</h2>
          <p className="text-sm text-muted-foreground">Manage educational content and blog posts</p>
        </div>
        <Button
          onClick={() => { resetForm(); setDialogOpen(true); }}
          style={{ backgroundColor: '#D4AF37', color: '#0F172A' }}
        >
          <Plus className="w-4 h-4 mr-2" /> Add Blog
        </Button>
      </div>

      <div className="grid gap-4">
        {blogs.length === 0 ? (
          <Card>
            <CardContent className="p-8 text-center">
              <BookOpen className="w-12 h-12 mx-auto mb-4 text-muted-foreground" />
              <p className="text-muted-foreground">No blogs yet. Add your first educational blog!</p>
            </CardContent>
          </Card>
        ) : (
          blogs.map((item) => (
            <Card key={item.blog_id} className={`${!item.is_active ? 'opacity-60' : ''}`}>
              <CardContent className="p-4">
                <div className="flex items-start gap-4">
                  {(item.thumbnail_url || item.media_url) && (
                    <div className="w-24 h-24 rounded-lg overflow-hidden flex-shrink-0 bg-gray-100">
                      {item.type === 'video' && item.media_url ? (
                        <video src={getImageUrl(item.media_url)} className="w-full h-full object-cover" />
                      ) : (
                        <img 
                          src={getImageUrl(item.thumbnail_url || item.media_url)} 
                          alt={item.title} 
                          className="w-full h-full object-cover" 
                        />
                      )}
                    </div>
                  )}
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2 mb-1 flex-wrap">
                      <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded text-xs font-medium" 
                        style={{ backgroundColor: 'rgba(212, 175, 55, 0.1)', color: '#D4AF37' }}>
                        {getTypeIcon(item.type)}
                        {item.type}
                      </span>
                      {!item.is_active && (
                        <span className="px-2 py-0.5 rounded text-xs font-medium bg-gray-100 text-gray-600">Hidden</span>
                      )}
                      {item.tags?.map(tag => (
                        <span key={tag} className="px-2 py-0.5 rounded text-xs font-medium bg-blue-50 text-blue-600">
                          {tag}
                        </span>
                      ))}
                    </div>
                    <h3 className="font-semibold text-lg truncate" style={{ color: '#0F172A' }}>{item.title}</h3>
                    {item.description && (
                      <p className="text-sm text-muted-foreground line-clamp-2">{item.description}</p>
                    )}
                    {item.external_link && (
                      <a href={item.external_link} target="_blank" rel="noopener noreferrer" 
                        className="inline-flex items-center gap-1 text-xs mt-1" style={{ color: '#D4AF37' }}>
                        <ExternalLink className="w-3 h-3" /> External Link
                      </a>
                    )}
                    <p className="text-xs text-muted-foreground mt-2">
                      Created: {formatDate(item.created_at)}
                      {item.created_by_name && ` by ${item.created_by_name}`}
                    </p>
                  </div>
                  <div className="flex items-center gap-2">
                    <Button variant="ghost" size="icon" onClick={() => toggleActive(item)}>
                      {item.is_active ? <Eye className="w-4 h-4" /> : <EyeOff className="w-4 h-4" />}
                    </Button>
                    <Button variant="ghost" size="icon" onClick={() => handleEdit(item)}>
                      <Edit className="w-4 h-4" />
                    </Button>
                    <Button variant="ghost" size="icon" onClick={() => handleDelete(item.blog_id)} className="text-red-500">
                      <Trash2 className="w-4 h-4" />
                    </Button>
                  </div>
                </div>
              </CardContent>
            </Card>
          ))
        )}
      </div>

      <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
        <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>{editMode ? 'Edit Blog' : 'Add Blog'}</DialogTitle>
          </DialogHeader>
          <form onSubmit={handleSubmit} className="space-y-4">
            <div className="space-y-2">
              <Label>Title *</Label>
              <Input
                value={formData.title}
                onChange={(e) => setFormData({ ...formData, title: e.target.value })}
                placeholder="Blog title"
                required
              />
            </div>

            <div className="space-y-2">
              <Label>Type *</Label>
              <Select value={formData.type} onValueChange={(value) => setFormData({ ...formData, type: value })}>
                <SelectTrigger>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="article">Article</SelectItem>
                  <SelectItem value="photo">Photo</SelectItem>
                  <SelectItem value="video">Video</SelectItem>
                  <SelectItem value="link">External Link</SelectItem>
                </SelectContent>
              </Select>
            </div>

            <div className="space-y-2">
              <Label>Description</Label>
              <Textarea
                value={formData.description}
                onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                placeholder="Brief description"
                rows={2}
              />
            </div>

            {formData.type === 'article' && (
              <div className="space-y-2">
                <Label>Blog Content</Label>
                <Textarea
                  value={formData.content}
                  onChange={(e) => setFormData({ ...formData, content: e.target.value })}
                  placeholder="Write your blog content here..."
                  rows={8}
                />
              </div>
            )}

            {(formData.type === 'photo' || formData.type === 'video') && (
              <div className="space-y-2">
                <Label>Upload {formData.type === 'photo' ? 'Photo' : 'Video'}</Label>
                <Input
                  type="file"
                  accept={formData.type === 'photo' ? 'image/*' : 'video/*'}
                  onChange={(e) => handleFileUpload(e.target.files[0], 'media_url')}
                  disabled={uploading}
                />
                {formData.media_url && (
                  <div className="mt-2">
                    {formData.type === 'video' ? (
                      <video src={getImageUrl(formData.media_url)} className="w-full max-h-48 object-contain rounded" controls />
                    ) : (
                      <img src={getImageUrl(formData.media_url)} alt="Preview" className="w-full max-h-48 object-contain rounded" />
                    )}
                  </div>
                )}
              </div>
            )}

            <div className="space-y-2">
              <Label>Thumbnail Image (optional)</Label>
              <Input
                type="file"
                accept="image/*"
                onChange={(e) => handleFileUpload(e.target.files[0], 'thumbnail_url')}
                disabled={uploading}
              />
              {formData.thumbnail_url && (
                <img src={getImageUrl(formData.thumbnail_url)} alt="Thumbnail" className="w-32 h-20 object-cover rounded mt-2" />
              )}
            </div>

            <div className="space-y-2">
              <Label>External Link</Label>
              <Input
                type="url"
                value={formData.external_link}
                onChange={(e) => setFormData({ ...formData, external_link: e.target.value })}
                placeholder="https://example.com/article"
              />
            </div>

            <div className="space-y-2">
              <Label>Tags</Label>
              <div className="flex gap-2">
                <Input
                  value={tagInput}
                  onChange={(e) => setTagInput(e.target.value)}
                  placeholder="Add a tag"
                  onKeyPress={(e) => e.key === 'Enter' && (e.preventDefault(), addTag())}
                />
                <Button type="button" variant="outline" onClick={addTag}>
                  <Tag className="w-4 h-4" />
                </Button>
              </div>
              {formData.tags.length > 0 && (
                <div className="flex flex-wrap gap-2 mt-2">
                  {formData.tags.map(tag => (
                    <span key={tag} className="inline-flex items-center gap-1 px-2 py-1 rounded text-sm bg-blue-50 text-blue-600">
                      {tag}
                      <button type="button" onClick={() => removeTag(tag)} className="hover:text-red-500">&times;</button>
                    </span>
                  ))}
                </div>
              )}
            </div>

            <div className="flex items-center gap-2">
              <input
                type="checkbox"
                id="is_active"
                checked={formData.is_active}
                onChange={(e) => setFormData({ ...formData, is_active: e.target.checked })}
                className="rounded"
              />
              <Label htmlFor="is_active">Publish (visible on landing page)</Label>
            </div>

            <div className="flex justify-end gap-2 pt-4">
              <Button type="button" variant="outline" onClick={() => setDialogOpen(false)}>
                Cancel
              </Button>
              <Button type="submit" style={{ backgroundColor: '#D4AF37', color: '#0F172A' }} disabled={uploading}>
                {uploading ? 'Uploading...' : (editMode ? 'Update' : 'Create')}
              </Button>
            </div>
          </form>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default AdminBlogsPage;
